import { ComponentRef, Injectable } from '@angular/core';
import { noop, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { enumHtsChatScope } from '@data-layer/enums/common';
import { HtsChatConversationModel, HtsChatEntryLineModel, HtsChatEntryModel, HtsChatRemovedLineModel } from '@data-layer/models/common';
import { HtsChatRepository } from '@data-layer/repositories';
import { ISidebarOptions } from '@interfaces/sidebar-options';
import { HtsAssistantDrawerComponent } from '../components/hts-assistant-drawer/hts-assistant-drawer.component';
import { HTS_CATALOG_WINDOW_Z_INDEX } from '../const/hts-assistant.const';
import { enumHtsAssistantMode } from '../enums';
import { HtsLineStatusHelper } from '../helpers/hts-line-status.helper';
import { IHtsAssistantLineRef } from '../interfaces';

export interface IHtsAssistantHistoryConfig {
  entrySummaryId: number;
  /** Picks the read endpoint: importer, broker or admin management. */
  scope: enumHtsChatScope;
  /** Admin, Broker Review (#13548): open on the final conversation only, with the "watch the do-over" toggle. */
  isDoOverToggle: boolean;
}

/** The read-only drawer just opened and a stream that ends with it. */
interface IHtsAssistantHistoryWindow {
  drawer: HtsAssistantDrawerComponent;
  destroyed$: Observable<void>;
}

/**
 * "Classification assistant history" (#13385): every conversation of the entry, read-only, one line at a time with
 * Back / Next. One entry-level GET per opening; the drawer gets the conversations, nothing is fetched per line.
 * A catalog item's history (#13554) is the same drawer with the item's conversations and no line.
 */
@Injectable({ providedIn: 'root' })
export class HtsAssistantHistoryService {
  private ref: ComponentRef<HtsAssistantDrawerComponent> | null = null;

  constructor(private appService: AppService, private htsChatRepository: HtsChatRepository) {}

  public open(config: IHtsAssistantHistoryConfig): void {
    this.htsChatRepository
      .getEntryHistory(config.entrySummaryId, config.scope)
      .pipe(take(1))
      .subscribe(
        (history: HtsChatEntryModel) => this.show(config, history),
        () => this.show(config, null),
      );
  }

  /** "History" in a catalog item's menu (#13554): a failed read is reported by the global alert, nothing opens. */
  public openForCatalogItem(favoriteHtsNumberId: number): void {
    this.htsChatRepository
      .getCatalogItemConversations(favoriteHtsNumberId)
      .pipe(take(1))
      .subscribe((conversations: HtsChatConversationModel[]) => this.showCatalogItem(conversations), noop);
  }

  public close(): void {
    const ref: ComponentRef<HtsAssistantDrawerComponent> | null = this.ref;
    this.ref = null;
    if (ref && this.appService.componentSidebarAsWindowRef === ref) {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    }
  }

  private show(config: IHtsAssistantHistoryConfig, history: HtsChatEntryModel | null): void {
    // the entry's lines first, then the lines deleted after they had conversations ("Removed lines", the broker's Shell
    // summary carries the same group)
    const historyLines: HtsChatEntryLineModel[] = history ? history.lines || [] : [];
    const removedLines: HtsChatRemovedLineModel[] = history ? history.removedLines || [] : [];
    const lines: IHtsAssistantLineRef[] = [
      ...HtsLineStatusHelper.fromHistory(historyLines),
      ...HtsLineStatusHelper.fromRemoved(removedLines, historyLines.length),
    ];
    const conversationsByLine: { [lineId: number]: HtsChatConversationModel[] } = {};
    const finalConversationIdByLine: { [lineId: number]: number | null } = {};
    historyLines.forEach((line: HtsChatEntryLineModel) => {
      conversationsByLine[line.lineId] = line.conversations || [];
      finalConversationIdByLine[line.lineId] = config.isDoOverToggle ? HtsLineStatusHelper.finalConversationId(line) : null;
    });
    removedLines.forEach((line: HtsChatRemovedLineModel) => {
      conversationsByLine[line.lineId] = line.conversations || [];
      // a removed line carries no verification: its latest conversation is the final one (#13548)
      finalConversationIdByLine[line.lineId] = config.isDoOverToggle ? HtsLineStatusHelper.latestConversationId(line.conversations) : null;
    });

    const { drawer, destroyed$ }: IHtsAssistantHistoryWindow = this.openWindow({
      sidebarHeaderText: '',
      isMd: true,
      isFillMobile: true,
    });
    const focus: IHtsAssistantLineRef | null = lines[0] || null;
    drawer.mode = enumHtsAssistantMode.Readonly;
    drawer.scope = config.scope;
    drawer.isDoOverToggle = config.isDoOverToggle;
    drawer.isHistoryFailed = history === null;
    drawer.lines = lines;
    drawer.conversationsByLine = conversationsByLine;
    drawer.finalConversationIdByLine = finalConversationIdByLine;
    drawer.lineId = focus ? focus.lineId : null;
    drawer.invoiceId = focus ? focus.invoiceId : null;
    drawer.selectedLineId = focus ? focus.lineId : null;
    drawer.lineDescription = focus ? focus.description : null;
    drawer.currentHts = focus ? focus.htsNumber : null;
    drawer.verificationSource = focus ? focus.verificationSource : null;
    drawer.verificationMatchedText = focus ? focus.matchedText : null;

    drawer.close.pipe(takeUntil(destroyed$)).subscribe(() => this.close());
    drawer.lineSelected.pipe(takeUntil(destroyed$)).subscribe((lineId: number) => {
      const line: IHtsAssistantLineRef | undefined = lines.find((l: IHtsAssistantLineRef) => l.lineId === lineId);
      if (line) {
        drawer.selectLine(line);
      }
    });
  }

  /** The catalog page's window level, no backdrop (the list stays in view) — as the live assistant opens there. */
  private showCatalogItem(conversations: HtsChatConversationModel[]): void {
    const { drawer, destroyed$ }: IHtsAssistantHistoryWindow = this.openWindow({
      sidebarHeaderText: '',
      isMd: true,
      isFillMobile: true,
      hideBackDrop: true,
      customZIndex: HTS_CATALOG_WINDOW_Z_INDEX,
    });
    drawer.mode = enumHtsAssistantMode.Readonly;
    drawer.scope = enumHtsChatScope.Importer;
    drawer.isCatalogMode = true;
    drawer.presetConversations = conversations;
    drawer.close.pipe(takeUntil(destroyed$)).subscribe(() => this.close());
  }

  /** The as-window slot (the previous history closed first) with the drawer's inputs still to be set. */
  private openWindow(options: ISidebarOptions): IHtsAssistantHistoryWindow {
    this.close();
    this.appService.isXlg$.next(true);
    this.ref = this.appService.openSidebarAsWindow(HtsAssistantDrawerComponent, options);

    const destroyed$: Subject<void> = new Subject<void>();
    this.ref.onDestroy(() => {
      destroyed$.next();
      destroyed$.complete();
    });
    return { drawer: this.ref.instance, destroyed$: destroyed$.asObservable() };
  }
}
